import React, { useCallback, useEffect, useRef,useState } from 'react'

import cx from 'classnames'

import { IconButton } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import styles from './styles.module.css'

interface Props {
  label: string
  initialValue: string
  placeholder?: string
  onSave: (newValue: string) => void
  className?: string
  saveOnInput?: boolean
  'data-ocweb-id'?: string
}

const EditableField: React.FC<Props> = ({
  label,
  className,
  initialValue,
  placeholder,
  onSave,
  saveOnInput,
  'data-ocweb-id': trackingId,
  ...rest
}) => {
  const [focused, setFocused] = useState(false)
  const [hovered, setHovered] = useState(false)
  const [value, setValue] = useState(initialValue)
  const ref = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setValue(initialValue)
    if (!saveOnInput) {
      setFocused(false)
      if (ref?.current) {
        ref.current.blur()
      }
    }
  }, [initialValue, setValue, setFocused, saveOnInput])

  const onSaveCb = useCallback(
    (e?: React.MouseEvent) => {
      // Cancel the bubbling, so we can call this function from the DOM or from another callback
      if (e) {
        e.preventDefault()
      }

      onSave(value)
      // Give the parent some time to update the initialValue
      setFocused(false)
      if (ref?.current) {
        ref.current.blur()
      }
    },
    [onSave, value, ref]
  )

  const onFocus = useCallback(() => {
    setFocused(true)
  }, [setFocused])

  const onCancel = useCallback(() => {
    setFocused(false)
    setValue(initialValue)
    if (ref?.current) {
      ref.current.blur()
    }
  }, [setFocused, setValue, initialValue, ref])

  const onHover = useCallback(() => {
    setHovered(true)
  }, [setHovered])

  const onUnhover = useCallback(() => {
    setHovered(false)
  }, [setHovered])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value)
      if (saveOnInput) {
        onSave(e.target.value)
      }
    },
    [saveOnInput, onSave]
  )

  const dirty = value !== initialValue

  const onBlur = useCallback(() => {
    if (!dirty) {
      setFocused(false)
    }
  }, [dirty, setFocused])

  const onKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        onSaveCb()
      }
    },
    [onSaveCb]
  )

  return (
    <div
      className={cx(styles.editableField, className, {
        [styles.placeholder]: !value && placeholder,
        [styles.focused]: focused,
        [styles.dirty]: dirty,
        [styles.hovered]: hovered
      })}
      {...rest}>
      <label>{label}</label>
      <div className={styles.valueContainer}>
        <input
          ref={ref}
          data-ocweb-id={trackingId}
          value={value}
          placeholder={placeholder}
          onChange={onChange}
          onFocus={onFocus}
          onKeyPress={onKeyPress}
          onMouseOver={onHover}
          onMouseOut={onUnhover}
          onBlur={onBlur}
        />
        {!saveOnInput && (
          <>
            <Icon className={styles.editIcon} name="edit-1" width={11} height={11} />
            <div className={styles.confirmCancelContainer}>
              <IconButton
                className={styles.checkIcon}
                buttonProps={{ onMouseDown: onSaveCb }}
                size="small"
                icon="check"
              />
              <IconButton
                className={styles.closeIcon}
                buttonProps={{ onMouseDown: onCancel }}
                size="small"
                icon="close"
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default EditableField
